import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  InternalTaskCustomField,
  InternalTaskFilterSet,
  InternalTaskFilters,
  TaskCustomStatus,
  TaskFilterSet,
  TaskLabel,
} from "@jugl-web/rest-api/tasks";
import { assert, reorder, useTranslations } from "@jugl-web/utils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useCallback, useMemo } from "react";

export interface UseTaskFieldsOptions {
  entityId: string | undefined;
  skipRemote?: boolean;
}

type NewTaskCustomField = Pick<
  InternalTaskCustomField,
  "name" | "type" | "isShownInCard" | "values"
>;

type NewTaskLabel = Pick<TaskLabel, "text" | "color">;

type NewCustomStatus = Pick<TaskCustomStatus, "text">;

const EMPTY_ARRAY: [] = [];

export const useTaskFields = ({
  entityId,
  skipRemote,
}: UseTaskFieldsOptions) => {
  const { tasksApi } = useRestApiProvider();
  const { t } = useTranslations();

  const {
    data: taskFields,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = tasksApi.useGetTaskFieldsQuery(
    entityId && !skipRemote ? { entityId } : skipToken,
    {
      refetchOnMountOrArgChange: false,
    }
  );

  // #region custom fields
  const [createTaskCustomFieldMutation, { isLoading: isCreatingCustomField }] =
    tasksApi.useCreateTaskCustomFieldMutation();

  const [
    updateTaskCustomFieldsMutation,
    { isLoading: isUpdatingCustomFields },
  ] = tasksApi.useUpdateTaskCustomFieldsMutation();

  const customFields = taskFields?.customFieldsData.items || EMPTY_ARRAY;

  const customFieldsMap = useMemo<Map<string, InternalTaskCustomField>>(
    () => new Map(customFields.map((field) => [field.id, field])),
    [customFields]
  );

  const getCustomFieldById = useCallback(
    (fieldId: string) => customFieldsMap.get(fieldId),
    [customFieldsMap]
  );

  const createCustomField = useCallback(
    (newCustomField: NewTaskCustomField) =>
      new Promise<void>((resolve, reject) => {
        if (!entityId || !taskFields) {
          reject();
          return;
        }

        createTaskCustomFieldMutation({
          entityId,
          customFieldsId: taskFields.customFieldsData.id,
          customField: {
            field: newCustomField.name,
            field_type: newCustomField.type,
            show_in_card: newCustomField.isShownInCard,
            order: taskFields.customFieldsData.items.length + 1,
            values: newCustomField.values,
          },
        }).then((response) => {
          if ("data" in response) {
            resolve();
          } else {
            reject();
          }
        });
      }),
    [createTaskCustomFieldMutation, entityId, taskFields]
  );

  const updateCustomFields = useCallback(
    (
      callback: (
        previousCustomFields: InternalTaskCustomField[]
      ) => InternalTaskCustomField[]
    ) =>
      new Promise<void>((resolve, reject) => {
        if (!entityId || !taskFields) {
          reject();
          return;
        }

        const updatedCustomFields = callback(taskFields.customFieldsData.items);

        updateTaskCustomFieldsMutation({
          entityId,
          customFieldsId: taskFields.customFieldsData.id,
          customFields: updatedCustomFields.map((internalField, index) => ({
            id: internalField.id,
            field: internalField.name,
            field_type: internalField.type,
            show_in_card: internalField.isShownInCard,
            order: index + 1,
            values: internalField.values,
          })),
        }).then((response) => {
          if ("data" in response) {
            resolve();
          } else {
            reject();
          }
        });
      }),
    [entityId, taskFields, updateTaskCustomFieldsMutation]
  );

  const updateCustomField = useCallback(
    (updatedCustomField: InternalTaskCustomField) =>
      updateCustomFields((previousCustomFields) =>
        previousCustomFields.map((field) =>
          field.id === updatedCustomField.id ? updatedCustomField : field
        )
      ),
    [updateCustomFields]
  );

  const deleteCustomField = useCallback(
    (customFieldId: string) =>
      updateCustomFields((previousCustomFields) =>
        previousCustomFields.filter((field) => field.id !== customFieldId)
      ),
    [updateCustomFields]
  );

  const reorderCustomField = useCallback(
    (startIndex: number, endIndex: number) =>
      updateCustomFields((previousCustomFields) =>
        reorder(previousCustomFields, startIndex, endIndex)
      ),
    [updateCustomFields]
  );
  // #endregion

  // #region labels
  const [createTaskLabelMutation, { isLoading: isCreatingLabel }] =
    tasksApi.useCreateTaskLabelMutation();

  const [updateTaskLabelMutation, { isLoading: isUpdatingLabel }] =
    tasksApi.useUpdateTaskLabelMutation();

  const [updateTaskLabelsInBulkMutation] =
    tasksApi.useUpdateTaskLabelsInBulkMutation();

  const [deleteTaskLabelMutation, { isLoading: isDeletingLabel }] =
    tasksApi.useDeleteTaskLabelMutation();

  const labels = taskFields?.labels || EMPTY_ARRAY;

  const labelsMap = useMemo<Map<string, TaskLabel>>(
    () => new Map(labels.map((label) => [label.id, label])),
    [labels]
  );

  const noneLabel = useMemo<TaskLabel>(
    () => ({
      id: "none",
      text: t({ id: "tasks-page.none-label", defaultMessage: "None" }),
      color: "#D2D2D8",
      order: 0,
    }),
    [t]
  );

  const getLabelById = useCallback(
    (labelId: string) => labelsMap.get(labelId),
    [labelsMap]
  );

  const createLabel = useCallback(
    async (newLabel: NewTaskLabel) => {
      assert(!!entityId);

      return new Promise<void>((resolve, reject) => {
        createTaskLabelMutation({
          entityId,
          label: {
            ...newLabel,
            order: labels.length > 0 ? labels[labels.length - 1].order + 1 : 1,
          },
        }).then((response) => {
          if ("data" in response) {
            resolve();
          } else {
            reject();
          }
        });
      });
    },
    [createTaskLabelMutation, entityId, labels]
  );

  const updateLabel = useCallback(
    (updatedLabel: TaskLabel) => {
      assert(!!entityId);
      return updateTaskLabelMutation({ entityId, label: updatedLabel });
    },
    [entityId, updateTaskLabelMutation]
  );

  const deleteLabel = useCallback(
    (labelId: string) => {
      assert(!!entityId);
      return deleteTaskLabelMutation({ entityId, labelId });
    },
    [entityId, deleteTaskLabelMutation]
  );

  const reorderLabel = useCallback(
    (startIndex: number, endIndex: number) => {
      assert(!!entityId);

      const updatedLabels = reorder(labels, startIndex, endIndex).map(
        (label, index) => ({ ...label, order: index + 1 })
      );

      updateTaskLabelsInBulkMutation({ entityId, labels: updatedLabels });
    },
    [entityId, labels, updateTaskLabelsInBulkMutation]
  );
  // #endregion

  // #region custom statuses
  const [
    createTaskCustomStatusMutation,
    { isLoading: isCreatingCustomStatus },
  ] = tasksApi.useCreateTaskCustomStatusMutation();

  const [
    updateTaskCustomStatusesMutation,
    { isLoading: isUpdatingCustomStatuses },
  ] = tasksApi.useUpdateTaskCustomStatusesMutation();

  const [
    deleteTaskCustomStatusMutation,
    { isLoading: isDeletingCustomStatus },
  ] = tasksApi.useDeleteTaskCustomStatusMutation();

  const customStatuses = taskFields?.customStatuses || EMPTY_ARRAY;

  const customStatusesMap = useMemo<Map<string, TaskCustomStatus>>(
    () => new Map(customStatuses.map((status) => [status.id, status])),
    [customStatuses]
  );

  const getCustomStatusById = useCallback(
    (customStatusId: string) => customStatusesMap.get(customStatusId),
    [customStatusesMap]
  );

  const updateCustomStatuses = useCallback(
    (
      callback: (
        previousCustomStatuses: TaskCustomStatus[]
      ) => TaskCustomStatus[]
    ) =>
      new Promise<void>((resolve, reject) => {
        if (!entityId || !taskFields) {
          reject();
          return;
        }

        const updatedCustomStatuses = callback(taskFields.customStatuses);

        updateTaskCustomStatusesMutation({
          entityId,
          customStatuses: updatedCustomStatuses,
        }).then((response) => {
          if ("data" in response) {
            resolve();
          } else {
            reject();
          }
        });
      }),
    [entityId, taskFields, updateTaskCustomStatusesMutation]
  );

  const createCustomStatus = useCallback(
    async (newCustomStatus: NewCustomStatus) => {
      assert(!!entityId);

      return new Promise<void>((resolve, reject) => {
        createTaskCustomStatusMutation({
          entityId,
          customStatus: {
            ...newCustomStatus,
            order:
              customStatuses.length > 0
                ? customStatuses[customStatuses.length - 1].order + 1
                : 1,
          },
        }).then((response) => {
          if ("data" in response) {
            resolve();
          } else {
            reject();
          }
        });
      });
    },
    [createTaskCustomStatusMutation, entityId, customStatuses]
  );

  const updateCustomStatus = useCallback(
    (statusId: string, updatedText: string) =>
      updateCustomStatuses((previousCustomStatuses) =>
        previousCustomStatuses.map((status) =>
          status.id === statusId ? { ...status, text: updatedText } : status
        )
      ),
    [updateCustomStatuses]
  );

  const deleteCustomStatus = useCallback(
    (customStatusId: string) => {
      assert(!!entityId);
      return deleteTaskCustomStatusMutation({
        entityId,
        statusId: customStatusId,
      });
    },
    [deleteTaskCustomStatusMutation, entityId]
  );

  const reorderCustomStatus = useCallback(
    (startIndex: number, endIndex: number) =>
      updateCustomStatuses((previousCustomStatuses) =>
        reorder(previousCustomStatuses, startIndex, endIndex).map(
          (status, index) => ({ ...status, order: index + 1 })
        )
      ),
    [updateCustomStatuses]
  );
  // #endregion

  // #region filter sets
  const [createTaskFilterSetMutation, { isLoading: isCreatingFilterSet }] =
    tasksApi.useCreateTaskFilterSetMutation();

  const [updateTaskFilterSetMutation, { isLoading: isUpdatingFilterSet }] =
    tasksApi.useUpdateTaskFilterSetMutation();

  const [deleteTaskFilterSetMutation, { isLoading: isDeletingFilterSet }] =
    tasksApi.useDeleteTaskFilterSetMutation();

  const filterSets = taskFields?.filterSets || EMPTY_ARRAY;

  const filterSetsMap = useMemo<Map<string, InternalTaskFilterSet>>(
    () => new Map(filterSets.map((filterSet) => [filterSet.id, filterSet])),
    [filterSets]
  );

  const getFilterSetById = useCallback(
    (filterSetId: string) => filterSetsMap.get(filterSetId),
    [filterSetsMap]
  );

  const createFilterSet = useCallback(
    (name: string, filters: InternalTaskFilters) => {
      assert(!!entityId);

      return new Promise<TaskFilterSet>((resolve, reject) => {
        createTaskFilterSetMutation({ entityId, name, filters }).then(
          (response) => {
            if ("data" in response) {
              resolve(response.data);
            } else {
              reject();
            }
          }
        );
      });
    },
    [createTaskFilterSetMutation, entityId]
  );

  const updateFilterSet = useCallback(
    (filterSetId: string, name: string, filters: InternalTaskFilters) => {
      assert(!!entityId);

      return new Promise<TaskFilterSet>((resolve, reject) => {
        updateTaskFilterSetMutation({
          entityId,
          filterSetId,
          name,
          filters,
        }).then((response) => {
          if ("data" in response) {
            resolve(response.data);
          } else {
            reject();
          }
        });
      });
    },
    [entityId, updateTaskFilterSetMutation]
  );

  const deleteFilterSet = useCallback(
    (filterSetId: string) => {
      assert(!!entityId);

      return new Promise<void>((resolve, reject) => {
        deleteTaskFilterSetMutation({ entityId, filterSetId }).then(
          (response) => {
            if ("data" in response) {
              resolve();
            } else {
              reject();
            }
          }
        );
      });
    },
    [deleteTaskFilterSetMutation, entityId]
  );
  // #endregion

  return {
    // Custom fields
    customFields,
    getCustomFieldById,
    createCustomField,
    updateCustomField,
    deleteCustomField,
    reorderCustomField,
    isCreatingCustomField,
    isUpdatingCustomFields,
    isDeletingCustomFields: isUpdatingCustomFields,

    // Labels
    labels,
    noneLabel,
    getLabelById,
    createLabel,
    updateLabel,
    deleteLabel,
    reorderLabel,
    isCreatingLabel,
    isUpdatingLabel,
    isDeletingLabel,

    // Custom statuses
    customStatuses,
    getCustomStatusById,
    createCustomStatus,
    updateCustomStatus,
    deleteCustomStatus,
    reorderCustomStatus,
    isCreatingCustomStatus,
    isUpdatingCustomStatuses,
    isDeletingCustomStatus,

    // Filter sets
    filterSets,
    getFilterSetById,
    createFilterSet,
    updateFilterSet,
    deleteFilterSet,
    isCreatingFilterSet,
    isUpdatingFilterSet,
    isDeletingFilterSet,

    // Task fields in general
    isLoading,
    isFetching,
    isError,
    refetch,
  };
};
